import { TFunction } from 'i18next';
import {
  formatSupportHours as composeSupportHours,
  SupportHoursCopy
} from 'Design-system/app/static/scripts/support-widget';
import { SupportState } from 'Douleutaras/redux/data/settings/supportSlice';

/**
 * The working-hours sentence, as the exit-intent sheet shows it.
 *
 * Composing it is the frontend's job — the endpoint publishes the schedule as values
 * because its response is cached across languages. The composition itself lives in the
 * design-system (`hours.ts`), shared with the Django widget: the two used to do it
 * separately and had already drifted on the time separator and on whether the weekdays
 * were sorted before being rendered as a range.
 *
 * This file is the translation half. It resolves the same msgids the Django template
 * fills into `#support-widget-copy`, and hands the templates over raw — the shared
 * formatter does the interpolation, so one translation serves both surfaces.
 *
 * This is formatting only. It makes no availability decision: whether support is open
 * is `state`, resolved server-side, and nothing here may second-guess it.
 */

const ISO_MONDAY = 1;
const ISO_SUNDAY = 7;

// Key literals stay at the call site so they remain greppable (i18n rule)
const weekdayName = (isoWeekday: number, t: TFunction): string => {
  switch (isoWeekday) {
    case 1: return t('T.SUPPORT-WIDGET.WEEKDAY-1', 'Monday');
    case 2: return t('T.SUPPORT-WIDGET.WEEKDAY-2', 'Tuesday');
    case 3: return t('T.SUPPORT-WIDGET.WEEKDAY-3', 'Wednesday');
    case 4: return t('T.SUPPORT-WIDGET.WEEKDAY-4', 'Thursday');
    case 5: return t('T.SUPPORT-WIDGET.WEEKDAY-5', 'Friday');
    case 6: return t('T.SUPPORT-WIDGET.WEEKDAY-6', 'Saturday');
    case 7: return t('T.SUPPORT-WIDGET.WEEKDAY-7', 'Sunday');
    default: return '';
  }
};

/**
 * The translated fragments, as the shared formatter wants them.
 *
 * Templates are requested with no interpolation values on purpose: `t()` returns them
 * with their placeholders intact and the shared formatter fills those in, which is what
 * lets `%(days)s` (Django's catalogue) and `{{days}}` both work from one msgid.
 */
export const supportHoursCopy = (t: TFunction): SupportHoursCopy => {
  const weekdays: { [isoWeekday: string]: string } = {};

  for (let day = ISO_MONDAY; day <= ISO_SUNDAY; day += 1) {
    weekdays[String(day)] = weekdayName(day, t);
  }

  return {
    hoursTemplate: t(
      'T.SUPPORT-WIDGET.HOURS',
      'Our team replies right away by phone or chat %(days)s, %(start)s – %(end)s.'
    ),
    dayRange: t('T.SUPPORT-WIDGET.DAY-RANGE', '%(first)s to %(last)s'),
    weekdays
  };
};

/** Empty when the schedule is not usable — callers drop the whole line. */
export const formatSupportHours = (schedule: SupportState['schedule'], t: TFunction): string =>
  composeSupportHours(schedule, supportHoursCopy(t));
